import { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ClerkProvider } from "@clerk/react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WatchlistProvider } from "@/context/WatchlistContext";
import { ContentExclusionProvider } from "@/context/ContentExclusionContext";
import { ClerkTokenBridge } from "@/components/ClerkTokenBridge";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { AppLayout } from "@/components/AppLayout";
import { LegalReacceptGate } from "@/components/LegalReacceptGate";
import { SiteGate } from "@/components/SiteGate";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { StepUpProvider } from "@/components/StepUpProvider";
import { BetaFeedbackButton } from "@/components/BetaFeedbackButton";
import { PageLoader } from "@/components/PageLoader";
import { LoadingScreen } from "@/components/LoadingScreen";
import { NavProgress } from "@/components/NavProgress";
import { CLERK_PUBLISHABLE_KEY } from "@/lib/clerkKey";
import { syncPreviewFreeFromUrl } from "@/lib/previewFree";

// Clerk migration Phase 2: fail loudly at startup if the key is missing rather
// than silently rendering a broken auth provider (CRA requires the REACT_APP_
// prefix; other bundler-specific env prefixes are silently dropped here).
// The key itself is chosen by hostname (preview pk_test_ vs. production
// pk_live_) in src/lib/clerkKey.js — see that file, not .env, for the live key.
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing REACT_APP_CLERK_PUBLISHABLE_KEY — set it in frontend/.env");
}

// Route-level code splitting: each page ships as its own chunk so the initial
// bundle stays small and pages load on demand. High-traffic routes are marked
// with webpackPrefetch so the browser grabs them during idle after first paint.
const Landing = lazy(() => import("@/pages/Landing"));
const HomePage = lazy(() => import("@/pages/home/HomePage"));
const DiscoverPublicPage = lazy(() => import("@/pages/discover/DiscoverPage"));
const TogetherPublicPage = lazy(() => import("@/pages/together-public/TogetherPublicPage"));
const BariPublicPage = lazy(() => import("@/pages/bari-public/BariPublicPage"));
const IntelligenceHubPublicPage = lazy(() => import("@/pages/intelligence-hub/IntelligenceHubPage"));
const RewindPublicPage = lazy(() => import("@/pages/rewind-public/RewindPublicPage"));
const PremierePublicPage = lazy(() => import("@/pages/premiere-public/PremierePage"));
const FriendsPublicPage = lazy(() => import("@/pages/friends-public/FriendsPage"));
const BannerHeaderConcept = lazy(() => import("@/pages/concept/BannerHeaderConcept"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Support = lazy(() => import("@/pages/Support"));
const SupportAccountAccess = lazy(() => import("@/pages/SupportAccountAccess"));
const ClerkSignIn = lazy(() => import("@/pages/ClerkSignIn"));
const ClerkSignUp = lazy(() => import("@/pages/ClerkSignUp"));
const Forgot = lazy(() => import("@/pages/Forgot"));
const Home = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Home"));
const Library = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Library"));
const LibraryActivity = lazy(() => import(/* webpackPrefetch: true */ "@/pages/library/LibraryActivity"));
const CatchUp = lazy(() => import("@/pages/CatchUp"));
const Tags = lazy(() => import("@/pages/Tags"));
const Discover = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Discover"));
const BestOnService = lazy(() => import("@/pages/BestOnService"));
const TrendingFull = lazy(() => import("@/pages/TrendingFull"));
const RecentlyAdded = lazy(() => import("@/pages/RecentlyAdded"));
const ComingSoonFull = lazy(() => import("@/pages/ComingSoonFull"));
const Search = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Search"));
const TitleDetail = lazy(() => import(/* webpackPrefetch: true */ "@/pages/TitleDetail"));
const Bari = lazy(() => import("@/pages/Bari"));
const BariChat = lazy(() => import("@/pages/BariChat"));
const ScreeningRoom = lazy(() => import("@/pages/ScreeningRoom"));
const ScreeningRoomBrowse = lazy(() => import(/* webpackPrefetch: true */ "@/pages/ScreeningRoomBrowse"));
const Circles = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Circles"));
const PeopleYouMayKnow = lazy(() => import("@/pages/PeopleYouMayKnow"));
const PicksForYouAll = lazy(() => import("@/pages/PicksForYouAll"));
const TrendingInCirclesAll = lazy(() => import("@/pages/TrendingInCirclesAll"));
const InviteRewards = lazy(() => import("@/pages/InviteRewards"));
const InviteLanding = lazy(() => import("@/pages/InviteLanding"));
const InviteClaim = lazy(() => import("@/pages/InviteClaim"));
const CircleDetail = lazy(() => import("@/pages/CircleDetail"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const Profile = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Profile"));
const MyReports = lazy(() => import("@/pages/MyReports"));
const ProfileSetup = lazy(() => import("@/pages/ProfileSetup"));
const SettingsRoot = lazy(() => import("@/pages/settings/SettingsRoot"));
const PublicList = lazy(() => import("@/pages/PublicList"));
const FeatureStub = lazy(() => import("@/pages/FeatureStub"));
const CollectionPage = lazy(() => import("@/pages/CollectionPage"));
const AwardSpotlight = lazy(() => import("@/pages/AwardSpotlight"));
const Decades = lazy(() => import("@/pages/Decades"));
const GenreBrowse = lazy(() => import("@/pages/GenreBrowse"));
const Tracker = lazy(() => import("@/pages/Tracker"));
const TrackerDetail = lazy(() => import("@/pages/TrackerDetail"));
const WatchParty = lazy(() => import("@/pages/WatchParty"));
const WatchPartyRoom = lazy(() => import("@/pages/WatchPartyRoom"));
const Matchmaker = lazy(() => import("@/pages/Matchmaker"));
const Optimizer = lazy(() => import("@/pages/Optimizer"));
const CatchMeUp = lazy(() => import("@/pages/CatchMeUp"));
const News = lazy(() => import("@/pages/News"));
const Rewind = lazy(() => import("@/pages/Rewind"));
const RewindAllPeople = lazy(() => import("@/pages/RewindAllPeople"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const TaggedInbox = lazy(() => import("@/pages/TaggedInbox"));
const Upgrade = lazy(() => import("@/pages/Upgrade"));
const Household = lazy(() => import("@/pages/Household"));
const Briefing = lazy(() => import("@/pages/Briefing"));
const BingePlanner = lazy(() => import("@/pages/BingePlanner"));
const Import = lazy(() => import("@/pages/Import"));
const Export = lazy(() => import("@/pages/Export"));
const ExportDownload = lazy(() => import("@/pages/ExportDownload"));
const Help = lazy(() => import("@/pages/Help"));
const ListDetail = lazy(() => import("@/pages/ListDetail"));
const MatchReveal = lazy(() => import("@/pages/MatchReveal"));
const CircleRank = lazy(() => import("@/pages/CircleRank"));
const AdminWaitlist = lazy(() => import("@/pages/AdminWaitlist"));
const AdminWatchPartyAnalytics = lazy(() => import("@/pages/AdminWatchPartyAnalytics"));
const Trivia = lazy(() => import("@/pages/Trivia"));
const TriviaRoom = lazy(() => import("@/pages/TriviaRoom"));
const AdminTrivia = lazy(() => import("@/pages/AdminTrivia"));
const Badges = lazy(() => import("@/pages/Badges"));
const GateAdmin = lazy(() => import("@/pages/GateAdmin"));
const AdminConsole = lazy(() => import("@/pages/AdminConsole"));
const VibeRooms = lazy(() => import("@/pages/VibeRooms"));
const VibeRoom = lazy(() => import("@/pages/VibeRoom"));
const WatchDuel = lazy(() => import("@/pages/WatchDuel"));
const AwardsPredictions = lazy(() => import("@/pages/AwardsPredictions"));
const FriendProfile = lazy(() => import(/* webpackPrefetch: true */ "@/pages/FriendProfile"));
const Gems = lazy(() => import("@/pages/Gems"));
const Alerts = lazy(() => import("@/pages/Alerts"));
const RecapShare = lazy(() => import("@/pages/RecapShare"));
const Attribution = lazy(() => import("@/pages/Attribution"));
const CommunityGuidelines = lazy(() => import("@/pages/CommunityGuidelines"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Tour = lazy(() => import("@/pages/Tour"));
const Person = lazy(() => import("@/pages/Person"));

// New IA destinations (Browse/Together/You/Library-preview/Legal hubs).
const BrowseHub = lazy(() => import("@/pages/browse/BrowseHub"));
const TogetherHub = lazy(() => import("@/pages/together/TogetherHub"));
const FriendsHub = lazy(() => import("@/pages/FriendsHub"));
const YouHub = lazy(() => import("@/pages/you/YouHub"));
const LibraryPreview = lazy(() => import("@/pages/library/LibraryPreview"));
const LibraryProviderDetail = lazy(() => import("@/pages/library/LibraryProviderDetail"));
const LegalHub = lazy(() => import("@/pages/LegalHub"));

// Legal & Trust Readiness (Phase 1) — pages that must stay reachable even
// while a member is blocked on the re-acceptance gate (sign-out, reading
// the public legal docs, and exporting/deleting data). /terms and /privacy
// are already outside Protected entirely (public routes).
const LEGAL_GATE_EXEMPT_PATHS = ["/app/export", "/app/settings/data-membership"];

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to={`/sign-in?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (user.needs_legal_reacceptance && !LEGAL_GATE_EXEMPT_PATHS.some((p) => loc.pathname.startsWith(p))) {
    return <AppLayout><LegalReacceptGate /></AppLayout>;
  }
  return <AppLayout>{children}</AppLayout>;
}

// Auth required but no app chrome — for full-screen flows like onboarding.
function AuthOnly({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to={`/sign-in?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
}

function InviteRedirect() {
  const { code } = useParams();
  return <Navigate to={`/sign-up?ref=${encodeURIComponent(code || "")}`} replace />;
}

// Cutover to Clerk: /login and /signup are no longer rendered pages — kept
// as redirects (not deleted) so distributed links/bookmarks still land
// somewhere, forwarding whatever query string they carried (?redirect=,
// ?ref=, etc.) straight through to the Clerk route.
function LegacyAuthRedirect({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}`} replace />;
}

// One-time branded splash on first paint: overlays the app (already mounting
// underneath) until auth has resolved, then pops + fades to reveal it.
function AppBoot({ children }) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(true);
  return (
    <>
      {visible && <LoadingScreen ready={user !== null} onDone={() => setVisible(false)} />}
      {children}
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/discover" element={<DiscoverPublicPage />} />
      <Route path="/together" element={<TogetherPublicPage />} />
      <Route path="/bari" element={<BariPublicPage />} />
      <Route path="/intelligence-hub" element={<IntelligenceHubPublicPage />} />
      <Route path="/rewind" element={<RewindPublicPage />} />
      <Route path="/premiere" element={<PremierePublicPage />} />
      <Route path="/friends" element={<FriendsPublicPage />} />
      <Route path="/concept/banner-header" element={<BannerHeaderConcept />} />
      <Route path="/tour" element={<Tour />} />
      <Route path="/login" element={<LegacyAuthRedirect to="/sign-in" />} />
      <Route path="/signup" element={<LegacyAuthRedirect to="/sign-up" />} />
      <Route path="/sign-in/*" element={<ClerkSignIn />} />
      <Route path="/sign-up/*" element={<ClerkSignUp />} />
      <Route path="/invite/:code" element={<InviteRedirect />} />
      <Route path="/i/:code" element={<InviteClaim />} />
      <Route path="/join/:token" element={<InviteLanding />} />
      <Route path="/forgot" element={<Forgot />} />
      <Route path="/list/:listId" element={<PublicList />} />
      <Route path="/recap/:token" element={<RecapShare />} />
      <Route path="/attribution" element={<Attribution />} />
      <Route path="/guidelines" element={<CommunityGuidelines />} />
      <Route path="/community-guidelines" element={<CommunityGuidelines />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/privacy-policy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/support" element={<Support />} />
      <Route path="/support/account-access" element={<SupportAccountAccess />} />
      <Route path="/app/welcome" element={<AuthOnly><Onboarding /></AuthOnly>} />

      <Route path="/app" element={<Protected><Home /></Protected>} />
      <Route path="/app/my-reports" element={<Protected><MyReports /></Protected>} />
      <Route path="/app/library" element={<Protected><Library /></Protected>} />
      <Route path="/app/library/activity" element={<Protected><LibraryActivity /></Protected>} />
      <Route path="/app/library/provider/:name" element={<Protected><LibraryProviderDetail /></Protected>} />
      <Route path="/app/catch-up" element={<Protected><CatchUp /></Protected>} />
      <Route path="/app/tags" element={<Protected><Tags /></Protected>} />
      <Route path="/app/screening-room" element={<Protected><Discover /></Protected>} />
      <Route path="/app/discover" element={<Navigate to="/app/screening-room" replace />} />
      <Route path="/app/best-on" element={<Protected><BestOnService /></Protected>} />
      <Route path="/app/trending" element={<Protected><TrendingFull /></Protected>} />
      <Route path="/app/new-releases" element={<Protected><RecentlyAdded /></Protected>} />
      <Route path="/app/coming-soon" element={<Protected><ComingSoonFull /></Protected>} />
      <Route path="/app/search" element={<Protected><Search /></Protected>} />
      <Route path="/app/title/:mediaType/:id" element={<Protected><TitleDetail /></Protected>} />
      <Route path="/app/worley" element={<Navigate to="/app/bari" replace />} />
      <Route path="/app/worley/chat" element={<Navigate to="/app/bari/chat" replace />} />
      <Route path="/app/bari" element={<Protected><Bari /></Protected>} />
      <Route path="/app/bari/chat" element={<Protected><BariChat /></Protected>} />
      <Route path="/app/screening-room-legacy" element={<Protected><ScreeningRoom /></Protected>} />
      <Route path="/app/catalog" element={<Protected><ScreeningRoomBrowse /></Protected>} />
      <Route path="/app/friends" element={<Protected><Circles /></Protected>} />
      <Route path="/app/people-you-may-know" element={<Protected><PeopleYouMayKnow /></Protected>} />
      <Route path="/app/picks-for-you" element={<Protected><PicksForYouAll /></Protected>} />
      <Route path="/app/trending-in-circles" element={<Protected><TrendingInCirclesAll /></Protected>} />
      <Route path="/app/circles" element={<Navigate to="/app/friends" replace />} />
      <Route path="/app/friend/:uid" element={<Protected><FriendProfile /></Protected>} />
      <Route path="/app/gems" element={<Protected><Gems /></Protected>} />
      <Route path="/app/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/app/invite" element={<Protected><InviteRewards /></Protected>} />
      <Route path="/app/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/app/circle/:id" element={<Protected><CircleDetail /></Protected>} />
      <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/app/profile/setup" element={<Protected><ProfileSetup /></Protected>} />
      <Route path="/app/settings/*" element={<Protected><SettingsRoot /></Protected>} />
      <Route path="/app/feature/:key" element={<Protected><FeatureStub /></Protected>} />
      <Route path="/app/collection/:key" element={<Protected><CollectionPage /></Protected>} />
      <Route path="/app/awards/:kind" element={<Protected><AwardSpotlight /></Protected>} />
      <Route path="/app/decades" element={<Protected><Decades /></Protected>} />
      <Route path="/app/genre/:name" element={<Protected><GenreBrowse /></Protected>} />
      <Route path="/app/tracker" element={<Protected><Tracker /></Protected>} />
      <Route path="/app/tracker/:kind/:id" element={<Protected><TrackerDetail /></Protected>} />
      <Route path="/app/watch-party" element={<Protected><WatchParty /></Protected>} />
      <Route path="/app/watch-party/:pid" element={<Protected><WatchPartyRoom /></Protected>} />
      <Route path="/app/vibe" element={<Protected><VibeRooms /></Protected>} />
      <Route path="/app/vibe/:rid" element={<Protected><VibeRoom /></Protected>} />
      <Route path="/app/duel" element={<Protected><WatchDuel /></Protected>} />
      <Route path="/app/duel/:sessionId" element={<Protected><WatchDuel /></Protected>} />
      <Route path="/app/awards-predictions" element={<Protected><AwardsPredictions /></Protected>} />
      <Route path="/app/matchmaker" element={<Protected><Matchmaker /></Protected>} />
      <Route path="/app/optimizer" element={<Protected><Optimizer /></Protected>} />
      <Route path="/app/catch-me-up" element={<Protected><CatchMeUp /></Protected>} />
      <Route path="/app/news" element={<Protected><News /></Protected>} />
      <Route path="/app/rewind" element={<Protected><Rewind /></Protected>} />
      <Route path="/app/rewind/people" element={<Protected><RewindAllPeople /></Protected>} />
      <Route path="/app/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/app/tagged" element={<Protected><TaggedInbox /></Protected>} />
      <Route path="/app/upgrade" element={<Protected><Upgrade /></Protected>} />
      <Route path="/app/household" element={<Protected><Household /></Protected>} />
      <Route path="/app/briefing" element={<Protected><Briefing /></Protected>} />
      <Route path="/app/binge" element={<Protected><BingePlanner /></Protected>} />
      <Route path="/app/import" element={<Protected><Import /></Protected>} />
      <Route path="/app/export" element={<Protected><Export /></Protected>} />
      <Route path="/app/export/download/:token" element={<Protected><ExportDownload /></Protected>} />
      <Route path="/app/help" element={<Protected><Help /></Protected>} />
      <Route path="/app/list/:id" element={<Protected><ListDetail /></Protected>} />
      <Route path="/app/match-reveal/:handle" element={<Protected><MatchReveal /></Protected>} />
      <Route path="/app/circle-rank/:handle" element={<Protected><CircleRank /></Protected>} />
      <Route path="/app/admin/waitlist" element={<Protected><AdminWaitlist /></Protected>} />
      <Route path="/app/admin/watch-party" element={<Protected><AdminWatchPartyAnalytics /></Protected>} />
      <Route path="/app/trivia" element={<Protected><Trivia /></Protected>} />
      <Route path="/app/trivia-room/:rid" element={<Protected><TriviaRoom /></Protected>} />
      <Route path="/app/admin/trivia" element={<Protected><AdminTrivia /></Protected>} />
      <Route path="/app/badges/:uid" element={<Protected><Badges /></Protected>} />
      <Route path="/app/admin/gate" element={<Protected><GateAdmin /></Protected>} />
      <Route path="/app/admin/console" element={<Protected><AdminConsole /></Protected>} />
      <Route path="/app/person/:id" element={<Protected><Person /></Protected>} />
      <Route path="/app/attribution" element={<Protected><Attribution /></Protected>} />

      {/* New IA destinations — Browse/Together/You/Library-preview/Legal hubs. See /app/memory/IA_REORG_PHASE2_NOTE.md */}
      <Route path="/legal" element={<LegalHub />} />
      <Route path="/app/browse" element={<Navigate to="/app/discover" replace />} />
      <Route path="/app/browse/best-on" element={<Protected><BestOnService /></Protected>} />
      <Route path="/app/browse/hidden-gems" element={<Protected><Gems /></Protected>} />
      <Route path="/app/browse/decades" element={<Protected><Decades /></Protected>} />
      <Route path="/app/browse/awards/:kind" element={<Protected><AwardSpotlight /></Protected>} />
      <Route path="/app/browse/genre/:name" element={<Protected><GenreBrowse hideBack /></Protected>} />
      <Route path="/app/browse/:key" element={<Protected><CollectionPage /></Protected>} />
      <Route path="/app/together" element={<Protected><TogetherHub /></Protected>} />
      <Route path="/app/friends-hub" element={<Protected><FriendsHub /></Protected>} />
      <Route path="/app/you" element={<Protected><YouHub /></Protected>} />
      <Route path="/app/library-preview" element={<Protected><LibraryPreview /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Clerk migration Phase 2: wires Clerk's own internal navigation (post
// sign-in/up, OAuth callback) to React Router's SPA navigate() instead of a
// hard window.location redirect — must render inside <BrowserRouter/>.
function ClerkProviderWithRouter({ children }) {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/app"
      signUpFallbackRedirectUrl="/app"
    >
      {children}
    </ClerkProvider>
  );
}

function App() {
  useEffect(() => {
    // Most pages already force their own scroll position on mount, so the
    // browser's native scroll restoration on back/forward only fights our
    // own logic (e.g. the Cast section restore in TitleDetail). Disable it
    // once, globally, so our explicit scroll handling is always the source of truth.
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    syncPreviewFreeFromUrl();
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkProviderWithRouter>
          <AuthProvider>
            <WatchlistProvider>
              <ClerkTokenBridge />
              <ConfirmProvider>
                <ErrorBoundary>
                  <SiteGate>
                    <StepUpProvider>
                      <NavProgress />
                      <AppBoot>
                        <ContentExclusionProvider>
                          <Suspense fallback={<PageLoader />}>
                            <AppRoutes />
                          </Suspense>
                        </ContentExclusionProvider>
                        <BetaFeedbackButton />
                      </AppBoot>
                    </StepUpProvider>
                  </SiteGate>
                </ErrorBoundary>
              </ConfirmProvider>
              <Toaster theme="dark" position="top-center" richColors />
            </WatchlistProvider>
          </AuthProvider>
        </ClerkProviderWithRouter>
      </BrowserRouter>
    </div>
  );
}

export default App;
